/**
 * Backdate-received_at confirmation modal.
 *
 * Spec: ``openspec/changes/add-procurement-backdate``. Opened from the
 * "Received at" row on the procurement detail page when ``row.state ===
 * 'received'``; lets the operator set a new historical moment for the
 * refill, subject to a server-enforced floor derived from finalized
 * stocktakes covering any line article (D1) and a global lock whenever
 * ANY stocktake is in-progress (D2).
 *
 * Flow:
 *   1. Picker defaults to ``current`` (the row's current received_at);
 *      ``disabledDate`` / ``disabledTime`` keep the user above
 *      ``received_at_min`` (when present).
 *   2. As the operator scrubs, we debounce-fetch ``/backdate-impact`` to
 *      render the impact summary (articles_affected, sales_in_window,
 *      pl_bucket_old → pl_bucket_new, optional buy-price-timeline resort
 *      heads-up). The preview is cheap and inline-reports a rejection
 *      reason (D6); we surface that without forcing a doomed PATCH.
 *   3. "I understand the cost-basis recalculation" checkbox guards the
 *      Confirm button.
 *   4. PATCH ``/api/procurement/{id}`` with ``{received_at}`` + the
 *      standard ``If-Match`` header. On 409 ``stocktake_in_progress``
 *      we close the modal (the page's centralised handler raises the
 *      blocking banner); on 409 ``received_at_below_floor`` we refresh
 *      the floor from the response body and keep the modal open so the
 *      operator can pick a value above the new floor.
 *   5. Cash-drawer note (D7) renders in the footer whenever the parent
 *      passes ``paid_in_cash`` so the operator knows the drawer ledger
 *      does NOT move with the refill.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  ApiError,
  type BackdateImpactResponse,
  type ProcurementRow,
  type ReceivedAtMinReason,
  api,
} from "../../api/client";
import { formatFullIso } from "../../utils/dateFormat";

const { Text, Paragraph } = Typography;

export interface BackdateReceivedAtModalProps {
  open: boolean;
  /** The procurement row being edited. We read ``id``, ``received_at``,
   * ``updated_at``, ``received_at_min``, and ``received_at_min_reason``
   * off this; the parent is responsible for re-fetching after success
   * (we hand the updated row back via ``onSuccess``). */
  row: ProcurementRow;
  /** Whether the procurement was paid in cash — drives the drawer-event
   * footer note (D7). The parent derives this from the linked
   * cash-drawer expense. */
  paidInCash: boolean;
  onClose: () => void;
  /** Called with the post-PATCH ProcurementRow so the parent can update
   * its own state without an extra GET. */
  onSuccess: (next: ProcurementRow) => void;
  /** Called when the parent should handle a 409 / 412 the same way the
   * rest of the page does (raise the stocktake_in_progress banner / the
   * stale banner). Modal closes itself after invoking. */
  onApiError: (err: unknown) => void;
}

/** Compute the earliest minute the picker should allow, given the
 * server-side ``received_at_min`` floor. AntD's ``disabledDate`` works
 * at day granularity; we feed a separate per-day ``disabledTime`` to
 * mask out the minutes above the floor on the floor day itself.
 *
 * The floor is parsed as a literal moment and compared in the picker's
 * own (local) zone — the operator picks local-time values. The picker
 * floor is rounded UP to the next minute when the server floor carries
 * a sub-minute component (the server floor is computed as
 * ``max(counted_at) + 1s`` so the seconds component is often non-zero,
 * but the picker's resolution is minutes). Rounding up avoids the UX
 * trap where a value that looks legal in the picker (e.g. 14:30:00)
 * gets rejected by the backend because the real floor is 14:30:15. The
 * backend remains the source of truth — preview + PATCH still check the
 * exact floor — but the picker guard is now consistent with what the
 * server will accept.
 *
 * DST note: ``dayjs(floorIso)`` parses an ISO 8601 string with explicit
 * offset (e.g. ``2026-03-29T01:00:00+00:00``) into a local-time Dayjs
 * pinned to the same instant. The picker also operates in local time,
 * so the hour/minute we read off the Dayjs match the user's clock for
 * that instant. On a DST transition day the local-time hour shifts by
 * one — that's the intended behaviour (the operator sees their wall
 * clock); the backend, which receives ISOs from the picker via
 * ``proposed.toISOString()``, compares instants regardless of zone.
 */
function buildPickerGuards(floorIso: string | null): {
  disabledDate: (d: Dayjs) => boolean;
  disabledTime: (d: Dayjs | null) => {
    disabledHours: () => number[];
    disabledMinutes: (h: number) => number[];
  };
} {
  if (!floorIso) {
    return {
      disabledDate: () => false,
      disabledTime: () => ({
        disabledHours: () => [],
        disabledMinutes: () => [],
      }),
    };
  }
  let floor = dayjs(floorIso);
  // Round UP to the next minute when seconds/ms are non-zero, so the
  // picker never offers a minute that the server will reject for being
  // a few seconds below the real floor. Subsecond components default to
  // 0 if absent in the ISO, so this is a no-op when the floor already
  // sits on a minute boundary.
  if (floor.second() !== 0 || floor.millisecond() !== 0) {
    floor = floor.add(1, "minute").startOf("minute");
  }
  const floorPinned = floor;
  return {
    // Block any whole day strictly before the floor's day.
    disabledDate: (d) =>
      d.startOf("day").isBefore(floorPinned.startOf("day")),
    disabledTime: (d) => {
      // Only restrict within the floor's own day. Days strictly above
      // the floor are unrestricted; days strictly below are blocked at
      // the date level above so this branch is unreachable for them.
      if (!d || !d.isSame(floorPinned, "day")) {
        return {
          disabledHours: () => [],
          disabledMinutes: () => [],
        };
      }
      const floorHour = floorPinned.hour();
      const floorMinute = floorPinned.minute();
      return {
        disabledHours: () => {
          const out: number[] = [];
          for (let h = 0; h < floorHour; h++) out.push(h);
          return out;
        },
        disabledMinutes: (h) => {
          if (h !== floorHour) return [];
          const out: number[] = [];
          for (let m = 0; m < floorMinute; m++) out.push(m);
          return out;
        },
      };
    },
  };
}

function formatReason(reason: ReceivedAtMinReason): string {
  return `stocktake #${reason.stocktake_id} on ${reason.article_name}`;
}

export const BackdateReceivedAtModal = ({
  open,
  row,
  paidInCash,
  onClose,
  onSuccess,
  onApiError,
}: BackdateReceivedAtModalProps) => {
  // The current received_at — locked even after a 409 refresh; only the
  // floor and the picker target move.
  const current = useMemo(
    () => (row.received_at ? dayjs(row.received_at) : null),
    [row.received_at],
  );

  // Floor mirrors the row but can be refreshed by a 409 below-floor
  // response body (the server returns the up-to-date floor + reason).
  const [floor, setFloor] = useState<string | null>(row.received_at_min);
  const [reason, setReason] = useState<ReceivedAtMinReason | null>(
    row.received_at_min_reason,
  );
  // Re-sync if the parent passes a new row in (e.g. after a refresh).
  useEffect(() => {
    setFloor(row.received_at_min);
    setReason(row.received_at_min_reason);
  }, [row.received_at_min, row.received_at_min_reason]);

  // Picker target — defaults to the current received_at so the operator
  // is editing a known value rather than a blank slate.
  const [proposed, setProposed] = useState<Dayjs | null>(current);
  useEffect(() => {
    // Reset to the current value whenever the modal opens fresh.
    if (open) setProposed(current);
  }, [open, current]);

  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  const [submitting, setSubmitting] = useState(false);
  // Inline error rendered above the picker (e.g. the PATCH server-side
  // rejection message after a refresh). Separate from ``onApiError``
  // which delegates to the parent for global banners.
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Debounced impact preview state.
  const [impact, setImpact] = useState<BackdateImpactResponse | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fire the debounced preview whenever the picker target changes (and
  // is a different moment than the current received_at — no point
  // previewing a no-op).
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!proposed) {
      setImpact(null);
      return;
    }
    if (current && proposed.isSame(current)) {
      setImpact(null);
      return;
    }
    const target = proposed.toISOString();
    debounceRef.current = setTimeout(async () => {
      setImpactLoading(true);
      try {
        const res = await api.backdateImpact(row.id, target);
        setImpact(res);
        // If the preview reports an updated floor (e.g. a stocktake
        // finalized while the modal was open), absorb it so the picker
        // guards stay in lock-step with the server.
        if (res.received_at_min !== floor) setFloor(res.received_at_min);
        if (res.received_at_min_reason !== reason) {
          setReason(res.received_at_min_reason);
        }
      } catch {
        // Preview is best-effort; a transient failure shouldn't block
        // the operator from confirming. The PATCH path will surface a
        // real error if there's one.
        setImpact(null);
      } finally {
        setImpactLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // floor / reason are intentionally NOT dependencies — we update them
    // INSIDE this effect, and re-running on every floor change would
    // restart the debounce timer mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposed, current, row.id]);

  const guards = useMemo(() => buildPickerGuards(floor), [floor]);

  // The picker target must be a real change, above the floor, with the
  // acknowledgement checked, before Confirm enables. The impact preview's
  // ``error_code`` (set when the server already knows the value would be
  // rejected) is also a hard block.
  const proposedSameAsCurrent =
    proposed && current ? proposed.isSame(current) : false;
  const previewBlocks = impact?.error_code != null;
  const confirmDisabled =
    !proposed ||
    proposedSameAsCurrent ||
    !acknowledged ||
    submitting ||
    previewBlocks;

  const onConfirm = useCallback(async () => {
    if (!proposed || proposedSameAsCurrent) return;
    setSubmitting(true);
    setInlineError(null);
    try {
      const next = await api.patchProcurementHeader(
        row.id,
        { received_at: proposed.toISOString() },
        row.updated_at,
      );
      onSuccess(next);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Two 409 flavours the parent CAN'T translate to a banner:
        //
        //   * received_at_below_floor — refresh our local floor from the
        //     response body and keep the modal open so the operator can
        //     pick a value above the new floor.
        //   * stocktake_in_progress / received_at_only_editable_on_received
        //     — delegate to the parent so the global banner / page state
        //     updates correctly, then close.
        const detail = extractDetail(err);
        if (
          detail &&
          typeof detail === "object" &&
          (detail as { error?: string }).error === "received_at_below_floor"
        ) {
          const d = detail as {
            error: string;
            floor?: string;
            reason?: ReceivedAtMinReason | null;
          };
          if (d.floor) setFloor(d.floor);
          if (d.reason !== undefined) setReason(d.reason ?? null);
          setInlineError(
            d.floor
              ? `Floor moved to ${formatFullIso(d.floor)}; pick a later moment.`
              : "Picked value is below the server-enforced floor.",
          );
          setSubmitting(false);
          return;
        }
        // Anything else 409 — hand off and close.
        onApiError(err);
        onClose();
        return;
      }
      if (err instanceof ApiError && err.status === 412) {
        onApiError(err);
        onClose();
        return;
      }
      // Generic failure — keep the modal open so the operator can retry.
      setInlineError(
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    proposed,
    proposedSameAsCurrent,
    row.id,
    row.updated_at,
    onSuccess,
    onClose,
    onApiError,
  ]);

  return (
    <Modal
      open={open}
      title="Change received-at datetime"
      onCancel={onClose}
      // Footer rendered manually so we can drop the drawer-event note
      // alongside the Confirm/Cancel buttons (D7).
      footer={null}
      destroyOnClose
      width={560}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {floor && reason && (
          <Alert
            type="info"
            showIcon
            message={
              <>
                Earliest allowed: <Text strong>{formatFullIso(floor)}</Text>{" "}
                — locked by {formatReason(reason)}.
              </>
            }
          />
        )}

        <Descriptions
          size="small"
          column={1}
          colon
          labelStyle={{ width: 140 }}
        >
          <Descriptions.Item label="Current">
            <Text>{current ? formatFullIso(current.toISOString()) : "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="New">
            <DatePicker
              value={proposed}
              showTime={{ format: "HH:mm" }}
              format="DD.MM.YYYY HH:mm"
              minuteStep={1}
              disabledDate={guards.disabledDate}
              disabledTime={guards.disabledTime}
              onChange={(d) => setProposed(d)}
              style={{ width: 260 }}
              allowClear={false}
            />
          </Descriptions.Item>
        </Descriptions>

        {inlineError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setInlineError(null)}
            message={inlineError}
          />
        )}

        <ImpactPanel
          loading={impactLoading}
          impact={impact}
          proposedSameAsCurrent={proposedSameAsCurrent}
          hasProposed={proposed != null}
        />

        <Checkbox
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        >
          I understand the cost-basis recalculation.
        </Checkbox>

        {paidInCash && (
          <Alert
            type="warning"
            showIcon
            message="Cash drawer event stays put"
            description="The linked cash drawer event will NOT move — it stays on the day cash physically crossed. Use the cash-drawer flow if that event also needs correction."
          />
        )}

        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => void onConfirm()}
            disabled={confirmDisabled}
            loading={submitting}
          >
            Confirm backdate
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

interface ImpactPanelProps {
  loading: boolean;
  impact: BackdateImpactResponse | null;
  proposedSameAsCurrent: boolean;
  hasProposed: boolean;
}

const ImpactPanel = ({
  loading,
  impact,
  proposedSameAsCurrent,
  hasProposed,
}: ImpactPanelProps) => {
  if (!hasProposed) {
    return (
      <Paragraph type="secondary" style={{ margin: 0 }}>
        Pick a new datetime to preview the impact.
      </Paragraph>
    );
  }
  if (proposedSameAsCurrent) {
    return (
      <Paragraph type="secondary" style={{ margin: 0 }}>
        Same as the current received-at — pick a different moment to
        preview the impact.
      </Paragraph>
    );
  }
  if (loading && !impact) {
    return (
      <Paragraph type="secondary" style={{ margin: 0 }}>
        Computing impact…
      </Paragraph>
    );
  }
  if (!impact) {
    return null;
  }
  return (
    <Descriptions
      size="small"
      column={1}
      colon
      bordered
      labelStyle={{ width: 200 }}
      title="Impact preview"
    >
      <Descriptions.Item label="Articles affected">
        {impact.articles_affected}
      </Descriptions.Item>
      <Descriptions.Item label="Sales in window">
        {impact.sales_in_window}
      </Descriptions.Item>
      <Descriptions.Item label="P&L bucket">
        {impact.pl_bucket_old ?? "—"}
        {" → "}
        {impact.pl_bucket_new ?? "—"}
      </Descriptions.Item>
      {impact.buy_price_timeline_resort && (
        <Descriptions.Item label="Buy-price timeline">
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Tag color="warning">Resort heads-up</Tag>
            <Text type="secondary">
              A later refill on{" "}
              {impact.resort_article_ids.length === 1
                ? "the same article"
                : `${impact.resort_article_ids.length} of the affected articles`}{" "}
              sits between the old and new received-at. The math stays
              consistent — the buy-price-at-time lookup tolerates
              arbitrary insertion order — but the timeline will read
              "later refill first" for those articles after this change.
            </Text>
          </Space>
        </Descriptions.Item>
      )}
      {impact.error_code && (
        <Descriptions.Item label="Server would reject">
          <Alert
            type="error"
            showIcon
            message={impactRejectionMessage(impact)}
          />
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

function impactRejectionMessage(impact: BackdateImpactResponse): string {
  switch (impact.error_code) {
    case "received_at_below_floor":
      return impact.received_at_min
        ? `Below the floor (${formatFullIso(impact.received_at_min)}).`
        : "Below the server-enforced floor.";
    case "stocktake_in_progress":
      return impact.stocktake_in_progress
        ? `Blocked: stocktake #${impact.stocktake_in_progress.stocktake_id} is currently in progress.`
        : "Blocked: a stocktake is currently in progress.";
    case "received_at_only_editable_on_received":
      return "received_at is only editable while the procurement is in the 'received' state.";
    default:
      return "Server would reject this value.";
  }
}

/** Unwrap FastAPI's ``{detail: ...}`` envelope from an ApiError body so
 * callers can inspect the structured ``error`` discriminator. Returns
 * the unwrapped detail or ``null`` if the shape doesn't match. */
function extractDetail(err: ApiError): unknown {
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    return (body as { detail: unknown }).detail;
  }
  return null;
}
